/**
 * AI Module - Journey Storage
 *
 * Persists generated journeys to localStorage.
 * Enables:
 * - Re-access from Dashboard Summary tab
 * - Change detection (regenerate if inputs changed)
 * - Avoid redundant API calls
 * - Journey history tracking
 */

import type { JourneyOutput } from '../schemas/journeyOutput.schema';
import { STORAGE_KEYS } from '../core/config';

// ============================================
// Types
// ============================================

export interface StoredJourney {
  journey: JourneyOutput;
  factPackHash: string;
  savedAt: string;
  version: string;
}

export interface JourneyHistoryEntry {
  id: string;
  factPackHash: string;
  headline: string;
  savedAt: string;
  isFallback: boolean;
}

// ============================================
// Journey Storage
// ============================================

/**
 * Save a journey to localStorage
 */
export function saveJourney(
  journey: JourneyOutput,
  factPackHash: string
): void {
  const stored: StoredJourney = {
    journey,
    factPackHash,
    savedAt: new Date().toISOString(),
    version: '1.0',
  };

  try {
    localStorage.setItem(STORAGE_KEYS.journey, JSON.stringify(stored));

    // Also add to history
    addToHistory({
      id: `journey_${Date.now()}`,
      factPackHash,
      headline: journey.summary.headline,
      savedAt: stored.savedAt,
      isFallback: !!journey._isFallback,
    });
  } catch (error) {
    console.warn('[Storage] Failed to save journey:', error);
  }
}

/**
 * Load the current journey from localStorage
 */
export function loadJourney(): StoredJourney | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.journey);
    if (!raw) return null;
    return JSON.parse(raw) as StoredJourney;
  } catch (error) {
    console.warn('[Storage] Failed to load journey:', error);
    return null;
  }
}

/**
 * Clear the stored journey
 */
export function clearJourney(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.journey);
  } catch (error) {
    console.warn('[Storage] Failed to clear journey:', error);
  }
}

/**
 * Check if we should regenerate the journey
 * Returns true if:
 * - No stored journey exists
 * - The Fact Pack has changed (different hash)
 */
export function shouldRegenerate(currentFactPackHash: string): boolean {
  const stored = loadJourney();
  if (!stored) return true;
  return stored.factPackHash !== currentFactPackHash;
}

/**
 * Get the age of the stored journey in milliseconds
 */
export function getJourneyAge(): number | null {
  const stored = loadJourney();
  if (!stored) return null;
  return Date.now() - new Date(stored.savedAt).getTime();
}

/**
 * Check if stored journey is a fallback
 */
export function isStoredJourneyFallback(): boolean {
  const stored = loadJourney();
  return !!stored?.journey._isFallback;
}

// ============================================
// Journey History
// ============================================

/**
 * Add an entry to journey history
 */
function addToHistory(entry: JourneyHistoryEntry): void {
  try {
    const history = loadHistory();
    history.unshift(entry);

    // Keep only last 10 entries
    const trimmed = history.slice(0, 10);
    localStorage.setItem(STORAGE_KEYS.journeyHistory, JSON.stringify(trimmed));
  } catch (error) {
    console.warn('[Storage] Failed to add to history:', error);
  }
}

/**
 * Load journey history
 */
export function loadHistory(): JourneyHistoryEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.journeyHistory);
    if (!raw) return [];
    return JSON.parse(raw) as JourneyHistoryEntry[];
  } catch (error) {
    console.warn('[Storage] Failed to load history:', error);
    return [];
  }
}

/**
 * Clear journey history
 */
export function clearHistory(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.journeyHistory);
  } catch (error) {
    console.warn('[Storage] Failed to clear history:', error);
  }
}
